<template>
  <v-card-text>
    <v-slider
      class="my-slider"
      v-model="localForm.jobSatisfactionLevel"
      :min="1"
      :max="10"
      step="1"
      label="Job Satisfaction Level (1-10)"
      thumb-label="always"
      ticks="always"
      tick-size="4"
      @update:model-value="updateForm"
    ></v-slider>

    <v-select
      :items="booleanNotSureOptions"
      v-model="localForm.helpsGrow"
      item-title="name"
      return-object="true"
      variant="outlined"
      :error-messages="getErrorMessages('helpsGrow').value"
      :is-required="isRequired('helpsGrow')"
      @blur="v$.helpsGrow?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Do you feel this job helps you grow professionally?
        <span v-if="isRequired('helpsGrow')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-slider
      class="my-slider"
      v-model="localForm.jobMatchSkills"
      :min="1"
      :max="5"
      step="1"
      label="Is this a good match for your skills? (1: Not at all - 5: Perfect match)"
      thumb-label="always"
      ticks="always"
      tick-size="4"
      @update:model-value="updateForm"
    ></v-slider>

    <v-select
      :items="booleanOptions"
      v-model="localForm.feelSecureInJob"
      item-title="name"
      item-value="value"
      variant="outlined"
      :error-messages="getErrorMessages('feelSecureInJob').value"
      :is-required="isRequired('feelSecureInJob')"
      @blur="v$.feelSecureInJob?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Do you feel secure in this job?
        <span v-if="isRequired('feelSecureInJob')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-slider
      class="my-slider"
      v-model="localForm.getAlong"
      :min="1"
      :max="5"
      step="1"
      label="How well do you get along with your team or managers? (1: Not well - 5: Very well)"
      thumb-label="always"
      ticks="always"
      tick-size="4"
      @update:model-value="updateForm"
    ></v-slider>

    <v-select
      v-model="localForm.recommendEmployer"
      :items="booleanNotSureOptions"
      item-title="name"
      return-object="true"
      variant="outlined"
      :error-messages="getErrorMessages('recommendEmployer').value"
      :is-required="isRequired('recommendEmployer')"
      @blur="v$.recommendEmployer?.$touch()"
      @update:model-value="updateForm"
    >
      <template #label>
        Would you recommend this employer to other beneficiaries?
        <span v-if="isRequired('recommendEmployer')" class="ml-1 red--text">
          <strong> *</strong>
        </span>
      </template>
    </v-select>

    <v-text-field
      v-if="localForm.recommendEmployer"
      v-model="localForm.recommendEmployerReasoning"
      label="Reasoning(Optional)"
      variant="outlined"
      :error-messages="getErrorMessages('recommendEmployerReasoning').value"
      @blur="v$.recommendEmployerReasoning?.$touch?.()"
      @input="updateForm"
    />
  </v-card-text>
</template>

<script setup>
import { watch, reactive, onMounted, computed } from "vue";
import useVuelidate from "@vuelidate/core";
import { defineProps, defineEmits } from "vue";

import { useListStore } from "@/stores";

const listStore = useListStore();

const props = defineProps({
  form: Object, // parent passes full form object
  rules: Object, // parent passes validation rules
});

const emit = defineEmits(["update:form"]);

// Emit changes back to parent
const updateForm = () => {
  emit("update:form", { ...localForm });
};

const localForm = reactive({ ...props.form });

const requiredFields = [
  "jobSatisfactionLevel",
  "helpsGrow",
  "jobMatchSkills",
  "feelSecureInJob",
  "getAlong",
  "recommendEmployer",
];

const completionPercent = computed(() => {
  const total = requiredFields.length;

  const filled = requiredFields.filter((field) => {
    return (
      localForm[field] !== null &&
      localForm[field] !== undefined &&
      localForm[field] !== ""
    );
  }).length;

  return Math.round((filled / total) * 100);
});

const isRequired = (field) => requiredFields.includes(field);

const v$ = useVuelidate(props.rules, localForm);

const getErrorMessages = (field) =>
  computed(() =>
    v$.value[field]?.$error
      ? v$.value[field].$errors.map((e) => e.$message)
      : []
  );

const booleanOptions = [
  { name: "Yes", value: true },
  { name: "No", value: false },
];

const booleanNotSureOptions = [
  { name: "Yes", value: "YES" },
  { name: "No", value: "NO" },
  { name: "Not sure", value: "NOT_SURE" },
];

// Watch parent updates in case they change
watch(
  () => props.form,
  (newVal) => {
    Object.assign(localForm, newVal);
  },
  { deep: true, immediate: true }
);

watch(
  completionPercent,
  (val) => {
    emit("update:completion", val);
  },
  { immediate: true }
);

onMounted(async () => {});
</script>

<style scoped>
/* 1. Reduce inactive track intensity */
::v-deep(.my-slider .v-slider-track__background) {
  background-color: rgba(255, 181, 90, 0.2) !important;
}

/* 2. Normal active track intensity */
::v-deep(.my-slider .v-slider-track__fill) {
  background-color: rgba(255, 181, 90, 0.5) !important;
}

/* 3. Strong active track when focusing/dragging */
::v-deep(.my-slider.v-slider--focused .v-slider-track__fill) {
  background-color: rgba(255, 181, 90, 1) !important;
}
</style>
